import React, { Fragment, useCallback, useRef, useState } from 'react';
import { FormControl } from 'react-bootstrap';
import _ from 'lodash';
import styles from './fileMultipleInput.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { faFileAlt } from '@fortawesome/free-regular-svg-icons';
import i18n from 'i18next';
import cx from 'classnames/bind';

const classNames = cx.bind(styles);

export type FileInfo = {
  file?: File;
  name: string;
  url?: string;
};

type FileMultipleInputProps = {
  onChange: (files: FileInfo[] | undefined) => void;
  validTypes?: string[];
  className?: string;
  name: string;
  disabled?: boolean;
  files?: FileInfo[];
};

// A generic (not image-only) multi-file picker modelled on ImageMultipleInput.
// Each selection/drop replaces the previous result (like ImageMultipleInput);
// the clear button removes everything and per-file removal is handled by the
// field wrapper's list.
export const FileMultipleInput: React.FC<FileMultipleInputProps> = ({
  onChange,
  validTypes,
  className,
  name,
  disabled,
  files
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [hovering, setHovering] = useState<boolean>(false);

  const setUpFilesBySelected = useCallback(
    (selected: File[]) => {
      if (!selected || selected.length === 0) {
        return;
      }
      const nextFiles: FileInfo[] = selected.map((file) => ({
        file,
        name: file.name
      }));
      onChange(nextFiles);
    },
    [onChange]
  );

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUpFilesBySelected(
      Array.from(_.get(event, 'currentTarget.files', []) as File[])
    );
    // reset so picking the same file again re-triggers change
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const onAreaClick = () => {
    inputRef.current && inputRef.current.click();
  };

  const onDragOver = (event: React.DragEvent) => {
    event.dataTransfer.dropEffect = 'copy';
    event.preventDefault();
  };

  const onDragEnter = (event: React.DragEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const onDrop = (event: React.DragEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setHovering(false);
    setUpFilesBySelected(
      Array.from(_.get(event, 'dataTransfer.files', []) as File[])
    );
  };

  const onClearBtnClicked = (event: React.MouseEvent) => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    onChange(undefined);
    setHovering(false);
    event.stopPropagation();
  };

  const renderPlaceholder = (withIcon: boolean) => {
    return (
      <div
        className={styles.filePlaceholder}
        onDragOver={onDragOver}
        onDragEnter={onDragEnter}
        onDrop={onDrop}
      >
        {withIcon && <FontAwesomeIcon icon={faFileAlt} size='3x' />}
        <div className={styles.hint}>
          {i18n.t('creativeSetupFlow.labels.dragdropHint')}
        </div>
      </div>
    );
  };

  const renderFilePreview = () => {
    return (
      <div
        className={styles.previewArea}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        onDragOver={onDragOver}
        onDragEnter={onDragEnter}
        onDrop={onDrop}
      >
        {hovering && (
          <Fragment>
            <div className={styles.mask} />
            {renderPlaceholder(false)}
            <div
              className={styles.closeBtnContainer}
              onClick={onClearBtnClicked}
            >
              <FontAwesomeIcon className={styles.closeBtn} icon={faTimes} />
            </div>
          </Fragment>
        )}
        <div className={styles.fileIcon}>
          <FontAwesomeIcon icon={faFileAlt} />
        </div>
      </div>
    );
  };

  const accept = validTypes ? validTypes.join(',') : undefined;
  const containerClassName = classNames('fileInputContainer', {
    [className as string]: !!className
  });

  return (
    <div className={containerClassName}>
      <FormControl
        accept={accept}
        className={styles.fileInput}
        type='file'
        ref={inputRef}
        name={name}
        disabled={disabled}
        onChange={onInputChange}
        multiple
      />
      <div className={styles.filePreview} onClick={onAreaClick}>
        {files && files.length > 0
          ? renderFilePreview()
          : renderPlaceholder(true)}
      </div>
    </div>
  );
};
